import { useState, ReactElement } from "react";
import "./ObjectTreeStruct.scss";
import {
  ServerAnalysisItem,
  MetaDataInfo,
  PdfInfo,
  DocInfo,
  MailInfo,
  PeVerInfo,
} from "../models/data-structures";
import { ReactComponent as OpenIcon } from "../assets/icons/add.svg";
import { ReactComponent as CloseIcon } from "../assets/icons/remove.svg";
import { ReactComponent as ArrowDown } from "../assets/icons/chevron-down.svg";
import { ReactComponent as ArrowLeft } from "../assets/icons/chevron-left.svg";
import { ReactComponent as MoreInfo } from "../assets/icons/align-left.svg";
import { getVerdictByRating, fileTypes } from "../logic/business-rules";

import { toKebabCase, toHumanReadableSize } from "../util/general";
import InnerCard from "../shared-ui-components/InnerCard";
import { ReactComponent as PEIcon } from "../assets/icons/inbox.svg";
import { ReactComponent as MailIcon } from "../assets/icons/envelope.svg";
import { ReactComponent as PDFIcon } from "../assets/icons/file.svg";
import { ReactComponent as DocIcon } from "../assets/icons/clip.svg";
import iconMapper from "../util/Category-icon-mapper";
import { WarningOutlined } from "@ant-design/icons";
import ToolTip from "../shared-ui-components/ToolTip";

interface TreeNodeProps {
  node: ServerAnalysisItem;
  name: string;
}

interface VBA {
  data: {
    VBASource?: string;
  };
}

const OpenTextFile: React.FC<VBA> = ({ data }) => {
  const openTextFile = () => {
    if (data.VBASource) {
      const blob = new Blob([data.VBASource], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    }
  };

  return (
    <div>
      {data.VBASource && (
        <div className="vba-button" onClick={openTextFile}>
          Open VBA code
        </div>
      )}
    </div>
  );
};

const TreeNode: React.FC<TreeNodeProps> = ({ node, name }) => {
  const isRoot = node.Id === node.ParentId;
  const [isOpen, setIsOpen] = useState(isRoot); // for shoing children under parent nodes
  const hasChildren = node.children && node.children.length > 0;
  const hasRating = node.Rating !== 0;
  const verdict = getVerdictByRating(node.Rating);
  const [showData, setShowData] = useState(false); // for node data

  const [showIndicator, setshowIndicator] = useState<Set<number>>(new Set()); // for indicators under nodes

  // for node data
  const toggleNode = () => {
    setShowData(!showData);
  };

  // for indicators under nodes
  const toggleIndicator = (index: number) => {
    setshowIndicator((prevState) => {
      const newSet = new Set(prevState);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Filter out 'Other' and 'Info' categories
  const filteredCats = node.IndicatorCategories!.filter(
    (cat) => cat.Category !== "Other" && cat.Category !== "Info",
  );
  const hasInfo = filteredCats.length > 0;

  // const filteredIndicatorCategories: ServerIndicatorCategory[] =
  //   filterCategories(node.IndicatorCategories!);

  // Update the node's IndicatorCategories with the filtered ones
  // node.IndicatorCategories = filteredIndicatorCategories;

  //Metdata section

  const PdfInfoComponent: React.FC<PdfInfo> = (data) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpansion = () =>
      setIsExpanded((prevIsExpanded) => !prevIsExpanded);

    const getNonEmptyFields = (pdfInfo: PdfInfo): [string, any][] => {
      return Object.entries(pdfInfo).filter(([key, value]) => {
        if (typeof value === "string") {
          return value.trim() !== "";
        }
        if (typeof value === "boolean") {
          return value === true;
        }
        if (typeof value === "object") {
          // Check for non-empty objects
          return value !== null && Object.keys(value).length > 0;
        }
        return value !== null && value !== undefined;
      });
    };

    const renderValue = (value: any): ReactElement | string => {
      if (typeof value === "boolean") {
        return value.toString();
      }
      if (typeof value === "object" && value !== null) {
        return (
          <ul>
            {Object.entries(value).map(([key, subValue]) => (
              <li>
                {key}: {renderValue(subValue)}
              </li>
            ))}
          </ul>
        );
      }
      return value.toString();
    };

    const NonEmptyFields = getNonEmptyFields(data);

    const includeViewMore = NonEmptyFields.length > 4;

    return (
      <InnerCard
        className={
          "metadata-overview-inner-card" + (isExpanded ? " expanded" : "")
        }
      >
        {{
          header: (
            <div className="metadata-overview-card-header">
              <div className="flex-start align-items-center">
                <div className="inner-card-header-icon">
                  <PDFIcon />
                </div>
                {/* <ToolTip
                children={categoryTitles[props.category] || props.category}
                content={categoryDescriptions[props.category] || props.category}
              /> */}
                <div>PDF Information</div>
              </div>
            </div>
          ),
          content: (
            <div
              className={
                "metadata-overview-card-content" +
                (isExpanded ? " expanded" : "")
              }
            >
              <div
                className={
                  (includeViewMore
                    ? "metadata-overview-card-findings-list flex-column"
                    : "metadata-overview-card-findings-list metadata-overview-card-pad no-mask") +
                  (isExpanded ? " expanded" : "")
                }
              >
                {NonEmptyFields.slice(
                  0,
                  isExpanded ? NonEmptyFields.length : 4,
                ).map(([key, value]) => (
                  <div key={key} className="meta-category-title">
                    {key}:{" "}
                    <div className="meta-category-value">
                      {renderValue(value)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex-space-between align-items-center">
                {/* <Button className={'expand-button'  + (isExpanded ? ' rotated' : '')} onClick={toggleExpansion} size="small" shape="circle" icon={<DownOutlined/>} /> */}
                {includeViewMore && (
                  <button className="yellow-link" onClick={toggleExpansion}>
                    {isExpanded ? "View less" : "View more"}
                  </button>
                )}
              </div>
            </div>
          ),
        }}
      </InnerCard>
    );
  };

  const DocInfoComponent: React.FC<DocInfo> = (data) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpansion = () =>
      setIsExpanded((prevIsExpanded) => !prevIsExpanded);

    const getNonEmptyFields = (docInfo: DocInfo): [string, any][] => {
      return Object.entries(docInfo).filter(([key, value]) => {
        if (typeof value === "string") {
          return value.trim() !== "";
        }
        if (typeof value === "boolean") {
          return value === true;
        }
        return value !== null && value !== undefined;
      });
    };

    const NonEmptyFields = getNonEmptyFields(data);

    const includeViewMore = NonEmptyFields.length > 4;

    return (
      <InnerCard
        className={
          "metadata-overview-inner-card" + (isExpanded ? " expanded" : "")
        }
      >
        {{
          header: (
            <div className="metadata-overview-card-header">
              <div className="flex-start align-items-center">
                <div className="inner-card-header-icon">
                  <DocIcon />
                </div>
                {/* <ToolTip
                children={categoryTitles[props.category] || props.category}
                content={categoryDescriptions[props.category] || props.category}
              /> */}
                <div>Document Info</div>
              </div>
            </div>
          ),
          content: (
            <div
              className={
                "metadata-overview-card-content" +
                (isExpanded ? " expanded" : "")
              }
            >
              <div
                className={
                  (includeViewMore
                    ? "metadata-overview-card-findings-list flex-column"
                    : "metadata-overview-card-findings-list metadata-overview-card-pad no-mask") +
                  (isExpanded ? " expanded" : "")
                }
              >
                {NonEmptyFields.slice(
                  0,
                  isExpanded ? NonEmptyFields.length : 4,
                ).map(([key, value]) => (
                  <div key={key} className="meta-category-title">
                    {key}:{" "}
                    <div className="meta-category-value">
                      {typeof value === "boolean" ? value.toString() : value}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex-space-between align-items-center">
                {/* <Button className={'expand-button'  + (isExpanded ? ' rotated' : '')} onClick={toggleExpansion} size="small" shape="circle" icon={<DownOutlined/>} /> */}
                {includeViewMore && (
                  <button className="yellow-link" onClick={toggleExpansion}>
                    {isExpanded ? "View less" : "View more"}
                  </button>
                )}
              </div>
            </div>
          ),
        }}
      </InnerCard>
    );
  };

  const MailInfoComponent: React.FC<MailInfo> = (data) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpansion = () =>
      setIsExpanded((prevIsExpanded) => !prevIsExpanded);

    const getNonEmptyFields = (mailInfo: MailInfo): [string, any][] => {
      return Object.entries(mailInfo).filter(([key, value]) => {
        if (typeof value === "string") {
          return value.trim() !== "";
        }
        if (typeof value === "boolean") {
          return value === true;
        }
        if (typeof value === "object") {
          // Check for non-empty objects
          return value !== null && Object.keys(value).length > 0;
        }
        return value !== null && value !== undefined;
      });
    };

    const renderValue = (value: any): ReactElement | string => {
      if (typeof value === "boolean") {
        return value.toString();
      }
      if (typeof value === "object" && value !== null) {
        return (
          <ul>
            {Object.entries(value).map(([key, subValue]) => (
              <li>
                {key}: {renderValue(subValue)}
              </li>
            ))}
          </ul>
        );
      }
      return value.toString();
    };

    const NonEmptyFields = getNonEmptyFields(data);

    const includeViewMore = NonEmptyFields.length > 4;

    return (
      <InnerCard
        className={
          "metadata-overview-inner-card" + (isExpanded ? " expanded" : "")
        }
      >
        {{
          header: (
            <div className="metadata-overview-card-header">
              <div className="flex-start align-items-center">
                <div className="inner-card-header-icon">
                  <MailIcon />
                </div>
                {/* <ToolTip
                children={categoryTitles[props.category] || props.category}
                content={categoryDescriptions[props.category] || props.category}
              /> */}
                <div>Mail Information</div>
              </div>
            </div>
          ),
          content: (
            <div
              className={
                "metadata-overview-card-content" +
                (isExpanded ? " expanded" : "")
              }
            >
              <div
                className={
                  (includeViewMore
                    ? "metadata-overview-card-findings-list flex-column"
                    : "metadata-overview-card-findings-list metadata-overview-card-pad no-mask") +
                  (isExpanded ? " expanded" : "")
                }
              >
                {NonEmptyFields.slice(
                  0,
                  isExpanded ? NonEmptyFields.length : 4,
                ).map(([key, value]) => (
                  <div key={key} className="meta-category-title">
                    {key}:{" "}
                    <div className="meta-category-value">
                      {renderValue(value)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex-space-between align-items-center">
                {/* <Button className={'expand-button'  + (isExpanded ? ' rotated' : '')} onClick={toggleExpansion} size="small" shape="circle" icon={<DownOutlined/>} /> */}
                {includeViewMore && (
                  <button className="yellow-link" onClick={toggleExpansion}>
                    {isExpanded ? "View less" : "View more"}
                  </button>
                )}
              </div>
            </div>
          ),
        }}
      </InnerCard>
    );
  };

  const PeVerInfoComponent: React.FC<PeVerInfo> = (data) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpansion = () =>
      setIsExpanded((prevIsExpanded) => !prevIsExpanded);

    const getNonEmptyFields = (info: PeVerInfo): [string, string][] => {
      return Object.entries(info).filter(([_, value]) => value.trim() !== "");
    };

    const NonEmptyFields = getNonEmptyFields(data);

    const includeViewMore = NonEmptyFields.length > 4;

    return (
      <InnerCard
        className={
          "metadata-overview-inner-card" + (isExpanded ? " expanded" : "")
        }
      >
        {{
          header: (
            <div className="metadata-overview-card-header">
              <div className="flex-start align-items-center">
                <div className="inner-card-header-icon">
                  <PEIcon />
                </div>
                {/* <ToolTip
                children={categoryTitles[props.category] || props.category}
                content={categoryDescriptions[props.category] || props.category}
              /> */}
                <div>Portable Executable Version Information</div>
              </div>
            </div>
          ),
          content: (
            <div
              className={
                "metadata-overview-card-content" +
                (isExpanded ? " expanded" : "")
              }
            >
              <div
                className={
                  (includeViewMore
                    ? "metadata-overview-card-findings-list flex-column"
                    : "metadata-overview-card-findings-list metadata-overview-card-pad no-mask") +
                  (isExpanded ? " expanded" : "")
                }
              >
                {NonEmptyFields.slice(
                  0,
                  isExpanded ? NonEmptyFields.length : 4,
                ).map(([key, value]) => (
                  <div key={key} className="meta-category-title">
                    {key}: <div className="meta-category-value">{value}</div>
                  </div>
                ))}
              </div>
              <div className="flex-space-between align-items-center">
                {/* <Button className={'expand-button'  + (isExpanded ? ' rotated' : '')} onClick={toggleExpansion} size="small" shape="circle" icon={<DownOutlined/>} /> */}
                {includeViewMore && (
                  <button className="yellow-link" onClick={toggleExpansion}>
                    {isExpanded ? "View less" : "View more"}
                  </button>
                )}
              </div>
            </div>
          ),
        }}
      </InnerCard>
    );
  };

  const MetadataComponent = (data: MetaDataInfo) => {
    return (
      <>
        {data.PdfInfo && <PdfInfoComponent {...data.PdfInfo} />}
        {data.DocInfo && <DocInfoComponent {...data.DocInfo} />}
        {data.MailInfo && <MailInfoComponent {...data.MailInfo} />}
        {data.PeVerInfo && Object.keys(data.PeVerInfo).length > 0 && (
          <PeVerInfoComponent {...data.PeVerInfo} />
        )}

        <OpenTextFile data={data} />

        {/*
        Make the VBA into a text file or something for the user to copy or download
        {data.VBASource && data.VBASource} */}
      </>
    );
  };

  // Indicators section
  // Render method for indicators
  const renderIndicators = () => {
    return filteredCats.map((indicator, index) => {
      // Calculate the highest rating for this category
      const highestRating = indicator.Indicators.reduce(
        (acc, value) => Math.max(acc, value.Rating),
        0,
      );
      return (
        <div key={index}>
          <div className="indicator-category">
            <div
              className="icon-title-chevron-container"
              onClick={() => toggleIndicator(index)}
            >
              <div className="icon-title">
                <IconComponent categoryKey={indicator.Category} />
                <div className="title">{indicator.Category}</div>
                {highestRating > 0 && (
                  <div
                    className={
                      "finding-rating " +
                      toKebabCase(getVerdictByRating(highestRating))
                    }
                  >
                    {highestRating.toFixed(0)}
                  </div>
                )}
              </div>
              <div className="chevron-indicator">
                {showIndicator.has(index) ? <ArrowDown /> : <ArrowLeft />}
              </div>
            </div>

            {showIndicator.has(index) && (
              <div className="indicator-data">
                {indicator.Indicators.map((value, indIndex) => (
                  <div className="ind-container">
                    <div
                      key={indIndex}
                      className="string-rating-booleans-container"
                    >
                      <span className="finding-text">
                        &#8226; {value.Description}
                      </span>
                      {value.Rating > 0 && (
                        <div
                          className={
                            "finding-rating " +
                            toKebabCase(getVerdictByRating(value.Rating))
                          }
                        >
                          {value.Rating.toFixed(0)}
                        </div>
                      )}
                      {/* when it is a internet category there is component data */}
                      {value.Components && (
                        <div className="component-booleans">
                          {value.Components.Hidden ? (
                            <div className="component-boolean">Hidden</div>
                          ) : (
                            ""
                          )}
                          {value.Components.Local ? (
                            <div className="component-boolean">Local</div>
                          ) : (
                            ""
                          )}
                          {value.Components.Punycode ? (
                            <div className="component-boolean">Punycode</div>
                          ) : (
                            ""
                          )}
                          {value.Components.Shortener ? (
                            <div className="component-boolean">Shortener</div>
                          ) : (
                            ""
                          )}
                        </div>
                      )}
                    </div>
                    {value.Components && (
                      <div className="component-strings">
                        {value.Components.Domain ? (
                          <span className="comp-key">
                            Domain:{" "}
                            <span className="comp-value">
                              {value.Components.Domain}
                            </span>
                          </span>
                        ) : (
                          ""
                        )}
                        {value.Components.Subdomain ? (
                          <span className="comp-key">
                            Subdomain:{" "}
                            <span className="comp-value">
                              {value.Components.Subdomain}
                            </span>
                          </span>
                        ) : (
                          ""
                        )}
                        {value.Components.Origin ? (
                          <span className="comp-key">
                            Origin:{" "}
                            <span className="comp-value">
                              {value.Components.Origin}
                            </span>
                          </span>
                        ) : (
                          ""
                        )}
                        {value.Components.Path ? (
                          <span className="comp-key">
                            Path:{" "}
                            <span className="comp-value">
                              {value.Components.Path}
                            </span>
                          </span>
                        ) : (
                          ""
                        )}
                        {value.Components.Directory ? (
                          <span className="comp-key">
                            Directory:{" "}
                            <span className="comp-value">
                              {value.Components.Directory}
                            </span>
                          </span>
                        ) : (
                          ""
                        )}
                        {value.Components.Query ? (
                          <span className="comp-key">
                            Query:{" "}
                            <span className="comp-value">
                              {value.Components.Query}
                            </span>
                          </span>
                        ) : (
                          ""
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    });
  };
  // TODO: Make a special case for internet indicator

  // use the iconmapper to have icons besides indicators categories

  const IconComponent = ({ categoryKey }: { categoryKey: string }) => {
    const key = categoryKey as keyof typeof iconMapper;
    const Component = iconMapper[key]?.component || WarningOutlined;
    return <Component />;
  };

  return (
    <div className={`treenode-container ${isRoot ? "root" : ""}`}>
      <div className="node-header">
        <div className="expand-icon-container">
          {hasChildren && !isRoot && (
            <span onClick={() => setIsOpen(!isOpen)} className="expand-icon">
              {isOpen ? <CloseIcon /> : <OpenIcon />}
            </span>
          )}
        </div>
        <div
          className={`node-object-container ${
            hasRating || verdict === "Unsupported" ? toKebabCase(verdict) : ""
          }`}
        >
          <div className="main-content" onClick={toggleNode}>
            <div className="name-rating-container">
              <div className="object-name">{name}</div>
              {hasRating && (
                <div
                  className={`object-rating ${
                    hasRating ? toKebabCase(verdict) : ""
                  }`}
                >
                  {node.Rating}
                </div>
              )}

              <ToolTip
                children={<div className="object-type">{node.ObjectType}</div>}
                content={fileTypes[node.ObjectType] || node.ObjectType}
              />
              {node.numberOfCopies! > 0 && <div>x{node.numberOfCopies}</div>}
            </div>
            <div className="num-chevron-container">
              {hasInfo && <MoreInfo className="more-info-icon" />}
              {hasChildren && (
                <div className="num-children">{node.children!.length}</div>
              )}
              {node.ObjectType !== "Folder" && (
                <div className="chevron">
                  {showData ? <ArrowDown /> : <ArrowLeft />}{" "}
                </div>
              )}
            </div>
          </div>

          {showData && node.ObjectType !== "Folder" && (
            <div className="additional-data">
              <div className="info-data-container">
                <div className="info-yellow-title">SHA256:</div> {node.Sha256}
              </div>
              <div className="info-data-container">
                <div className="info-yellow-title">Object Size:</div>
                {toHumanReadableSize(node.ObjectSize)}
              </div>
              {node.MetaData && (
                <div className="metadata-section">
                  <MetadataComponent {...node.MetaData} />
                </div>
              )}
              {node.IndicatorCategories && hasInfo && (
                <div>
                  <div className="info-yellow-title indicators">
                    Indicators:
                  </div>

                  {renderIndicators()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {isOpen && hasChildren && (
        <div className="children-container">
          {node.children?.map((childNode, index) => (
            <TreeNode
              key={childNode.Id}
              name={childNode.Name}
              node={childNode}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TreeNode;
